﻿import { Injectable, OnDestroy } from '@angular/core';
import { ValidatorFn } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef, MatProgressBar } from '@angular/material';
import { MediaObserver } from '@angular/flex-layout';

import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { CheckboxDialogComponent, BaseCheckboxDialogOptions } from './checkbox/checkbox.dialogcomponent';
import { CountdownDialogComponent, BaseCountdownDialogOptions } from './countdown/countdown.dialogcomponent';
import { ConfirmationDialogComponent, BaseConfirmationDialogOptions } from './confirmation/confirmation.dialogcomponent';
import { InputBoxDialogComponent, BaseInputBoxDialogOptions } from './inputbox/inputbox.dialogcomponent';
import { LoginBoxDialogComponent, BaseLoginBoxDialogOptions, BaseLoginBoxDialogResult } from './loginbox/loginbox.dialogcomponent';
import { MessageBoxDialogComponent, BaseMessageBoxDialogOptions } from './messagebox/messagebox.dialogcomponent';
import { NotificationDialogComponent, BaseNotificationDialogOptions } from './notification/notification.dialogcomponent';
import {
    OutOfOfficeDesigneeSearchDialogComponent,
    BaseOOODesigneeSearchDialogOptions,
    BaseOOODesigneeSearchAcceptOption,
    BaseOOODesigneeSearchResult,
} from './ooodesigneesearch/ooodesigneesearch.dialogcomponent';
import { OptionBoxDialogComponent, BaseOptionBoxDialogOptions, BaseOptionBoxOption } from './optionbox/optionbox.dialogcomponent';
import {
    ReportOptionsDialogComponent,
    BaseReportOptionsDialogOptions,
    BaseReportOptionsResult,
} from './reportoptions/reportoptions.dialogcomponent';
import {
    SelectBoxDialogComponent,
    BaseSelectBoxDialogOptions,
    BaseSelectBoxSelectionOption,
} from './selectbox/selectbox.dialogcomponent';
import {
    SelectBoxNoteDialogComponent,
    BaseSelectBoxNoteDialogOptions,
    BaseSelectBoxNoteSelectionOption,
    BaseSelectBoxNoteResult,
} from './selectboxnote/selectboxnote.dialogcomponent';
import {
    SubordinateDialogComponent,
    BaseSubordinateDialogOptions,
    BaseSubordinateDialogResult,
} from './subordinate/subordinate.dialogcomponent';
import { TextNoteDialogComponent, BaseTextNoteDialogOptions, BaseTextNoteDialogResult } from './textnote/textnote.dialogcomponent';
import {
    TimeApproveDialogComponent,
    BaseTimeApproveDialogOptions,
    BaseTimeApproveDialogResult,
} from './timeapprove/timeapprove.dialogcomponent';
import {
    TimeRequestDialogComponent,
    BaseTimeRequestDialogOptions,
    BaseTimeRequestResult,
} from './timerequest/timerequest.dialogcomponent';
import {
    UserSearchDialogComponent,
    BaseUserSearchDialogOptions,
    BaseUserSearchAcceptOption,
    BaseUserSearchResult,
} from './usersearch/usersearch.dialogcomponent';
import { EmployeeInfo, UserSearchResult } from 'services/application/application.service';
import { NotificationDetail } from 'services/notification/notification.service';

export { BaseCountdownDialogOptions as CountdownDialogOptions };
export { BaseConfirmationDialogOptions as ConfirmationDialogOptions };
export { BaseInputBoxDialogOptions as InputBoxDialogOptions };
export { BaseLoginBoxDialogOptions as LoginBoxDialogOptions };
export { BaseLoginBoxDialogResult as LoginBoxDialogResult };
export { BaseMessageBoxDialogOptions as MessageBoxDialogOptions };
export { BaseNotificationDialogOptions as NotificationDialogOptions };
export { BaseOptionBoxDialogOptions as OptionBoxDialogOptions };
export { BaseOptionBoxOption as OptionBoxOption };
export { BaseOOODesigneeSearchDialogOptions as OOODesigneeSearchDialogOptions };
export { BaseReportOptionsDialogOptions as ReportOptionsDialogOptions };
export { BaseReportOptionsResult as ReportOptionsResult };
export { BaseSelectBoxDialogOptions as SelectBoxDialogOptions };
export { BaseSelectBoxSelectionOption as SelectBoxSelectionOption };
export { BaseSelectBoxNoteDialogOptions as SelectBoxNoteDialogOptions };
export { BaseSelectBoxNoteResult as SelectBoxNoteResult };
export { BaseSelectBoxNoteSelectionOption as SelectBoxNoteSelectionOption };
export { BaseSubordinateDialogOptions as SubordinateDialogOptions };
export { BaseSubordinateDialogResult as SubordinateDialogResult };
export { BaseTextNoteDialogOptions as TextNoteDialogOptions };
export { BaseTextNoteDialogResult as TextNoteDialogResult };
export { BaseTimeRequestDialogOptions as TimeRequestDialogOptions };
export { BaseTimeRequestResult as TimeRequestResult };
export { BaseUserSearchDialogOptions as UserSearchDialogOptions };

@Injectable({
    providedIn: 'root',
})
export class DialogService {
    private openHandle: MatDialogRef<any>;
    private _subrelease: Subject<void> = new Subject<void>();

    smallMediaSize: boolean;

    constructor(private matDialog: MatDialog, private mediaChange: MediaObserver) {
        this.smallMediaSize = mediaChange.isActive('screen and (max-width: 959px)');

        // Observe for media changes
        this.mediaChange.media$.pipe(takeUntil(this._subrelease)).subscribe(media => {
            // Set flags accordingly with what we know.
            this.smallMediaSize = media.mqAlias === 'xs' || media.mqAlias === 'sm';
        });
    }

    // #region Close All Dialogs Service Call

    CloseAllDialogs() {
        this.matDialog.closeAll();
    }

    // #endregion

    // #region Open Checkbox Dialog Service Call

    OpenCheckboxDialog(options?: BaseCheckboxDialogOptions): Observable<boolean> {
        const openHandle = this.matDialog.open<CheckboxDialogComponent>(CheckboxDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Information Requested',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            width: options.width || '800px',
            height: options.height || '400px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Confirmation Dialog Service Call

    OpenConfirmationDialog(options?: BaseConfirmationDialogOptions): Observable<boolean> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'Confirmation Requested...',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            panelClass: 'confdialog',
            maxHeight: '85vh',
            width: self.smallMediaSize ? '80vw' : options.width || '600px',
            height: self.smallMediaSize ? '80vh' : options.height || '400px',
            // width: '80vw'
            // ,
            // height: (self.smallMediaSize ? '80vh' : '600px')
        };

        if (self.smallMediaSize) {
            // dlgoptions.width = '80vw';
            // dlgoptions.height = '80vh';
            dlgoptions.panelClass = ['confdialog', 'smalldialogpanel'];
        }

        const openHandle = this.matDialog.open<ConfirmationDialogComponent>(ConfirmationDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Countdown Dialog Service Call

    OpenCountdownDialog(options?: BaseCountdownDialogOptions): Observable<boolean> {
        const self = this;

        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'Please Wait...',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                Time: 60,
                ...options,
            },
            width: '500px',
            height: '18rem',
            maxHeight: '85vh',
        };

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            // dlgoptions.height = '80vh';
            dlgoptions.height = '30rem';
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<CountdownDialogComponent>(CountdownDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open OOO Designee Search Dialog Service Call

    OpenOOODesigneeSearchDialog(options?: BaseOOODesigneeSearchDialogOptions): Observable<BaseOOODesigneeSearchResult> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'User Search',
                MessageText: '',
                OKText: 'Select',
                CancelText: 'Cancel',
                ...options,
            },
            maxHeight: '85vh',
            width: self.smallMediaSize ? '80vw' : options.width || '800px',
            height: self.smallMediaSize ? '80vh' : options.height || '400px',
        };

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            dlgoptions.height = '80vh';
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<OutOfOfficeDesigneeSearchDialogComponent>(
            OutOfOfficeDesigneeSearchDialogComponent,
            dlgoptions
        );

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Input Box Dialog Service Call

    OpenInputBoxDialog(options?: BaseInputBoxDialogOptions): Observable<string> {
        const openHandle = this.matDialog.open<InputBoxDialogComponent>(InputBoxDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Information Requested...',
                MessageText: '',
                InputHint: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            maxHeight: '85vh',
            // width: '500px',
            // height: (options && options.height) || '13rem'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Login Box Dialog Service Call

    OpenLoginBoxDialog(options?: BaseLoginBoxDialogOptions): Observable<BaseLoginBoxDialogResult> {
        const openHandle = this.matDialog.open<LoginBoxDialogComponent>(LoginBoxDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Credentials Required...',
                MessageText: '',
                InputHint: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            maxHeight: '85vh',
            // width: '500px',
            // height: (options && options.height) || '13rem'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Message Box Dialog Service Call

    OpenMessageBoxDialog(options?: BaseMessageBoxDialogOptions): Observable<boolean> {
        const self = this;

        const openHandle = self.matDialog.open<MessageBoxDialogComponent>(MessageBoxDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Information',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            width: self.smallMediaSize ? '80vw' : options.width || '600px',
            height: self.smallMediaSize ? '80vh' : options.height || '400px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Notification Dialog Service Call

    OpenNotificationDialog(options?: BaseNotificationDialogOptions): Observable<NotificationDetail> {
        const self = this;

        const openHandle = self.matDialog.open<NotificationDialogComponent>(NotificationDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Notification Details',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            width: self.smallMediaSize ? '80vw' : options.width || '900px',
            height: self.smallMediaSize ? '80vh' : options.height || '400px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Option Box Dialog Service Call

    OpenOptionBoxDialog(options?: BaseOptionBoxDialogOptions): Observable<any> {
        const openHandle = this.matDialog.open<OptionBoxDialogComponent>(OptionBoxDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Please select',
                MessageText: '',
                Options: [],
                ...options,
            },
            maxHeight: '85vh',
            // width: '500px',
            // height: (options && options.height) || '13rem'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Report Options Dialog Service Call

    OpenReportOptionsDialog(options?: BaseReportOptionsDialogOptions): Observable<BaseReportOptionsResult> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'Choose Report Options...',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            maxHeight: '85vh', // ,
            // width: '80vw'
            // ,
            // height: (self.smallMediaSize ? '80vh' : '600px')
        };

        if (options.width) {
            dlgoptions.width = options.width;
        }

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            // dlgoptions.height = '80vh';
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<ReportOptionsDialogComponent>(ReportOptionsDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Select Box Dialog Service Call

    OpenSelectBoxDialog(options?: BaseSelectBoxDialogOptions): Observable<any> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'Make Choice...',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            maxHeight: '85vh',
            width: self.smallMediaSize ? '80vw' : options.width || '600px',
            height: self.smallMediaSize ? '80vh' : options.height || '400px',
        };

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            dlgoptions.height = '80vh';
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<SelectBoxDialogComponent>(SelectBoxDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Select Box Note Dialog Service Call

    OpenSelectBoxNoteDialog(options?: BaseSelectBoxNoteDialogOptions): Observable<BaseSelectBoxNoteResult> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'Make Choice...',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            maxHeight: '85vh',
            width: self.smallMediaSize ? '80vw' : options.width || '600px',
            height: self.smallMediaSize ? '80vh' : options.height || '400px',
        };

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            dlgoptions.height = '80vh';
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<SelectBoxNoteDialogComponent>(SelectBoxNoteDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Subordinate Dialog Service Call

    OpenSubordinateDialog(options?: BaseSubordinateDialogOptions): Observable<BaseSubordinateDialogResult> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'Make Choice...',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            maxHeight: '85vh',
            width: self.smallMediaSize ? '80vw' : options.width || '600px',
            height: self.smallMediaSize ? '80vh' : options.height || '400px',
        };

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            dlgoptions.height = '80vh';
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<SubordinateDialogComponent>(SubordinateDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Text Note Dialog Service Call

    OpenTextNoteDialog(options?: BaseTextNoteDialogOptions): Observable<BaseTextNoteDialogResult> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'Optional Note',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            maxHeight: '85vh',
            width: self.smallMediaSize ? '80vw' : options.width || '600px',
            height: self.smallMediaSize ? '80vh' : options.height || '400px',
        };

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            dlgoptions.height = '80vh';
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<TextNoteDialogComponent>(TextNoteDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Time Approve Dialog Service Call

    OpenTimeApproveDialog(options?: BaseTimeApproveDialogOptions): Observable<BaseTimeApproveDialogResult> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'Mark Board Time Approval',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            maxHeight: '85vh',
            width: self.smallMediaSize ? '80vw' : options.width || '600px',
            height: self.smallMediaSize ? '80vh' : options.height || '400px',
        };

        if (self.smallMediaSize) {
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<TimeApproveDialogComponent>(TimeApproveDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Time Request Dialog Service Call

    OpenTimeRequestDialog(options?: BaseTimeRequestDialogOptions): Observable<BaseTimeRequestResult> {
        const self = this;
        const openHandle = this.matDialog.open<TimeRequestDialogComponent>(TimeRequestDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Request Time Approval',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            width: self.smallMediaSize ? '80vw' : options.width || '600px',
            height: self.smallMediaSize ? '80vh' : options.height || '400px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open User Search Dialog Service Call

    OpenUserSearchDialog(options?: BaseUserSearchDialogOptions): Observable<BaseUserSearchResult> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'User Search',
                MessageText: '',
                OKText: 'Select',
                CancelText: 'Cancel',
                ...options,
            },
            maxHeight: '85vh',
            width: self.smallMediaSize ? '80vw' : options.width || '800px',
            height: self.smallMediaSize ? '80vh' : options.height || '400px',
        };

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            dlgoptions.height = '80vh';
            dlgoptions.panelClass = 'smalldialogpanel';
        }

        const openHandle = this.matDialog.open<UserSearchDialogComponent>(UserSearchDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion
}

// #region Custom Classes

// #region Base Dialog Options Class

export class BaseDialogOptions {
    TitleText?: string;
    MessageText?: string;
    OKText?: string;
    CancelText?: string;
    IconColor?: string;
    IconName?: string;
    ShowIcon?: boolean;
    height?: string;
    width?: string;
}

// #endregion

// #endregion
